import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Bugsnag from '@bugsnag/js';
import cx from 'classnames';
import CompanyAwards from '../CompanyAwards/CompanyAwards';
import Avatar from '../../../ui-elements/Avatar/Avatar';
import Link from '../../../ui-elements/Link/Link';
import Loading from '../../../ui-elements/Loading/Loading';
import Alert from '../../../ui-elements/Alert/Alert';

import { GET_API_LMD_URL, GET_KELDAN_API_URL } from '../../../services/config';
import { getTableDate, insertDash, displayAddress } from '../../../services/utils';

import {
  IAPI1819,
  ICompanyViewModel,
  KeldanCompanyInfoStripped,
} from '../../../types/CompanyTypes';
import { IKeldanApiResponse } from '../../../types/KeldanTypes';
import AvatarLoading from '../../../ui-elements/Avatar/AvatarLoading';

type Props = ICompanyViewModel & {
  showLoading?: boolean;
  disableMargin?: boolean;
  withLinkToCompanyPage?: boolean;
};

interface CompanyData {
  companyName: string;
  kennitala: string;
  address: string;
  phoneNumber: string;
  phoneLink: string;
  isatId: string;
  isatDescription: string;
  companyRepresentative: string;
  logoUrl?: string;
  registrationLabel: string;
  registrationValue: string;
  isDeregistered: boolean;
  isBankrupt: boolean;
  rawKeldanData: KeldanCompanyInfoStripped | null;
}

const CompanyTitle: React.FC<Props> = ({ id, accessToken, showLoading = false, disableMargin = false, withLinkToCompanyPage = false }) => {
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const formatValue = useCallback((value: string | number | null | undefined): string => {
    return value === null || value === undefined || value.toString().trim() === '' ? '-' : value.toString();
  }, []);

  const formatPhoneNumber = useCallback((phoneNum: string | undefined): string => {
    if (!phoneNum) return '-';
    return phoneNum.length === 7
      ? `${phoneNum.slice(0, 3)}-${phoneNum.slice(3)}`
      : phoneNum;
  }, []);

  const getRegistrationDisplay = useCallback(
    (data: KeldanCompanyInfoStripped): { label: string; value: string } => {
      const registeredDate = getTableDate(data.DateRegistered ?? '', 'DD/MM/YYYY', '.');
      if (data.DateDeregistered) {
        const deregisteredDate = getTableDate(data.DateDeregistered, 'DD/MM/YYYY', '.');
        return { label: 'Skráð / Afskráð', value: `${registeredDate}\u00A0/ ${deregisteredDate}` };
      }
      if (data.DateBankrupt) {
        const bankruptDate = getTableDate(data.DateBankrupt, 'DD/MM/YYYY', '.');
        return { label: 'Skráð / Gjaldþrota', value: `${registeredDate}\u00A0/ ${bankruptDate}` };
      }
      return { label: 'Skráð', value: registeredDate };
    },
    []
  );

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [keldanResp, api1819Resp] = await Promise.allSettled([
          fetch(`${GET_API_LMD_URL()}/v1/market_data/v1/keldan/company/${id}/basic_info`, {
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          }),
          fetch(`${GET_KELDAN_API_URL()}/Company/GetAPI1819/${id}`),
        ]);

        if (!isMounted) return;

        let keldanData: KeldanCompanyInfoStripped | null = null;
        if (keldanResp.status === 'fulfilled' && keldanResp.value.ok) {
          const json = (await keldanResp.value.json()) as KeldanCompanyInfoStripped[];
          keldanData = json[0] || null;
        }

        if (!keldanData) {
          throw new Error('No company data found');
        }

        let api1819Data: IAPI1819 | null = null;
        if (api1819Resp.status === 'fulfilled' && api1819Resp.value.ok) {
          const json = (await api1819Resp.value.json()) as IKeldanApiResponse<IAPI1819[]>;
          api1819Data = json.result?.[0] || null;
        }

        const phone = api1819Data?.phones?.[0]?.phone?.toString();
        const phoneLink = api1819Data?.link || 'https://1819.is/';
        const registration = getRegistrationDisplay(keldanData);
        const representative = keldanData.ChairmanName || keldanData.BoardmemberName;

        const data: CompanyData = {
          companyName: formatValue(keldanData.CompanyName),
          kennitala: formatValue(insertDash(keldanData.CompanyId?.toString())),
          address: formatValue(displayAddress(keldanData.CompanyAddress, keldanData.CompanyPostalCode, keldanData.CompanyCity)),
          phoneNumber: phone ? formatPhoneNumber(phone) : '1819.is',
          phoneLink,
          isatId: formatValue(keldanData.IsatId),
          isatDescription: formatValue(keldanData.IsatDescriptionIs),
          companyRepresentative: formatValue(representative),
          logoUrl: api1819Data?.logo,
          registrationLabel: registration.label,
          registrationValue: registration.value,
          isDeregistered: !!keldanData.DateDeregistered,
          isBankrupt: !!keldanData.DateBankrupt,
          rawKeldanData: keldanData,
        };

        if (isMounted) setCompanyData(data);
      } catch (err) {
        const e = err instanceof Error ? err : new Error('An unexpected error occurred');
        Bugsnag.notify(e);
        if (isMounted) setError(e);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    id !== undefined && fetchData();
    return () => {
      isMounted = false;
    };
  }, [id, accessToken, formatValue, formatPhoneNumber, getRegistrationDisplay]);

  const renderValue = useCallback(
    (value?: string | null) =>
      isLoading && showLoading ? <Loading width='120' count={1} /> : <>{formatValue(value)}</>,
    [isLoading, showLoading, formatValue]
  );

  const fields: FieldItem[] = useMemo(() => {
    let list: (FieldItem | false | null | undefined)[] = [];
    list = [
      { title: 'Fyrirtæki', value: renderValue(companyData?.companyName) },
      { title: 'Kennitala', value: renderValue(companyData?.kennitala) },
      { title: 'Póstfang', value: renderValue(companyData?.address) },
      {
        title: 'Símanúmer',
        value: (
          <Link linkSize="15" url={companyData?.phoneLink} target="_blank">
            {renderValue(companyData?.phoneNumber)}
          </Link>
        ),
      },
      { title: 'Ísat', value: renderValue(companyData?.isatId) },
      { title: 'Ísat flokkur', value: renderValue(companyData?.isatDescription) },
      !companyData?.isDeregistered &&
      !companyData?.isBankrupt && {
        title: 'Forráðamaður',
        value: renderValue(companyData?.companyRepresentative),
      },
      {
        title: companyData?.registrationLabel ?? 'Skráð',
        value: renderValue(companyData?.registrationValue),
      },
    ];

    return list.filter((f): f is FieldItem => Boolean(f));
  }, [companyData, renderValue]);


  if (error) return <Alert type="error" headText={error.message} />;
  if (!companyData && !isLoading) return <Alert type="error" headText="No company data available" />;

  type FieldItem = {
    title: string;
    value: React.ReactNode;
  };


  return (
    <div
      className={cx(
        "KCL_company-title",
        { "noMargin": disableMargin }
      )}
    >
      {fields.map((f, idx) => (
        <div key={idx} className="info-col">
          {f.title === 'Fyrirtæki' && (
            isLoading ? (
              <AvatarLoading size="lg" />
            ) : companyData?.logoUrl ? (
              <Avatar dataLink={companyData.logoUrl} size="lg" />
            ) : null
          )}
          <div className="info-item">
            <span className="title">{f.title}</span>
            <span
              className={cx("value", {
                isatCategory: f.title === 'Ísat flokkur' || f.title === 'Forráðamaður',
                companyName: f.title === 'Fyrirtæki',
              })}
            >
              {f.title === 'Fyrirtæki' && withLinkToCompanyPage ? <Link linkSize='24' url={`/Fyrirtaeki/Yfirlit/${id}`}>{f.value}</Link> : f.value}
            </span>
          </div>
        </div>
      ))}
      {id &&
        <CompanyAwards id={id} />
      }
    </div>
  );
};

export default React.memo(CompanyTitle);
